import type { ObjectTypeDefinition } from "daedalus-core";
import { CHEST, GATE, SHRINE, WELL } from "../editor/type-names.ts";

/**
 * The sounds of world things (ADR-085's amendment for P1.9-T79): a thing's change of state plays
 * `sfx/world/<sound set>/<event>`, the event the verb of the transition it made. The sound set is the one the thing
 * names when its behaviour type lists it, otherwise the type's default, otherwise none, which is silent. The table is
 * the game's and keyed by the type's id, so the editor's object types carry nothing about sound.
 */

/**
 * The folder every thing's sound set lies in.
 */
const WORLD_FOLDER = "sfx/world";

/**
 * Each behaviour type's sound sets, the first its default. A type with no entry is silent.
 */
export type ThingSoundSets = Readonly<Record<string, readonly string[]>>;

/**
 * The game's sound sets: a gate sounds as a wooden door unless it names the iron gate or the wooden fence by its
 * "Sound set" field (ADR-085's amendment of 2026-10-13, `thing-sound-field.ts`), a chest as a wooden chest (ADR-091
 * decision 17), which sounds as it opens (`open`) and as it shuts (`shut`), a shrine as a shrine (ADR-096 decision
 * 28), and a well as a well, as its bucket comes up (`raise`) and as it goes down (`lower`).
 */
export const THING_SOUND_SETS: ThingSoundSets = Object.freeze({
  [GATE]: Object.freeze(["wooden_door", "iron_gate", "wooden_fence"]),
  [CHEST]: Object.freeze(["wooden_chest"]),
  [SHRINE]: Object.freeze(["shrine"]),
  [WELL]: Object.freeze(["well"]),
});

/**
 * The events a type sounds on when they are not its verbs: a shrine's state never changes on the wire, so its one
 * sound is the `pulse` an arrival's event plays (`sfx/world/shrine/pulse`), fired by the page from `thingEvent`. A
 * type with no entry sounds on its transitions' verbs (`thingEvents`).
 */
export const THING_EVENT_SOUNDS: Readonly<Record<string, readonly string[]>> = Object.freeze({ [SHRINE]: Object.freeze(["pulse"]) });

/**
 * A sound set's hooks as the hook list reads them: the set, the events it sounds on, and the set each of its hooks
 * falls back to, or null for a type's default.
 */
export interface ThingHookSource {
  readonly set: string;
  readonly events: readonly string[];
  readonly fallback: string | null;
}

/**
 * A thing's hook for an event.
 *
 * @param set The sound set, a folder under `sfx/world/`.
 * @param event The event, a transition's verb.
 * @returns The hook, `sfx/world/<set>/<event>`.
 */
export function thingHook(set: string, event: string): string {
  return `${WORLD_FOLDER}/${set}/${event}`;
}

/**
 * The sound set a thing plays from: the one it names when its type lists it, otherwise its type's default.
 *
 * @param type The thing's behaviour type.
 * @param named The set the thing itself names, or undefined for none.
 * @param sets Each type's sound sets; the game's by default.
 * @returns The set, or null when the type has none, which is silent.
 * @example
 * thingSoundSet("gate");
 * // "wooden_door"
 */
export function thingSoundSet(type: string, named?: string, sets: ThingSoundSets = THING_SOUND_SETS): string | null {
  const listed = sets[type];
  if (listed === undefined || listed.length === 0) {
    return null;
  }
  return named !== undefined && listed.includes(named) ? named : (listed[0] as string);
}

/**
 * The events a type sounds on: the verbs of its transitions between two different states, each once in declaration
 * order.
 *
 * @param type The object type.
 * @returns The verbs.
 */
export function thingEvents(type: Pick<ObjectTypeDefinition, "transitions">): string[] {
  const verbs: string[] = [];
  for (const transition of type.transitions) {
    if (transition.from !== transition.to && !verbs.includes(transition.verb)) {
      verbs.push(transition.verb);
    }
  }
  return verbs;
}

/**
 * The event a thing's change of state is: the verb of its type's first transition between the two states.
 *
 * @param types The game's object types.
 * @param type The thing's behaviour type.
 * @param from The id of the state it left.
 * @param to The id of the state it took.
 * @returns The verb, or null when the type is unknown, the state did not change or no transition joins the two.
 */
export function thingEvent(types: readonly ObjectTypeDefinition[], type: string, from: string, to: string): string | null {
  if (from === to) {
    return null;
  }
  const definition = types.find((candidate) => candidate.type === type);
  return definition?.transitions.find((transition) => transition.from === from && transition.to === to)?.verb ?? null;
}

/**
 * The hook a thing's change of state plays.
 *
 * @param types The game's object types.
 * @param type The thing's behaviour type.
 * @param from The id of the state it left.
 * @param to The id of the state it took.
 * @param named The sound set the thing itself names, or undefined for none.
 * @param sets Each type's sound sets; the game's by default.
 * @returns The hook, or null when the type has no sound set or the change is no event.
 * @example
 * thingSound(ODYSSEY_ADAPTER.objectTypes, "gate", "closed", "open");
 * // "sfx/world/wooden_door/open"
 */
export function thingSound(types: readonly ObjectTypeDefinition[], type: string, from: string, to: string, named?: string, sets: ThingSoundSets = THING_SOUND_SETS): string | null {
  const set = thingSoundSet(type, named, sets);
  const event = set === null ? null : thingEvent(types, type, from, to);
  return set === null || event === null ? null : thingHook(set, event);
}

/**
 * Every sound set's hooks for the hook list: each set of each type that has any, sounding on the type's events (its
 * `THING_EVENT_SOUNDS` when it has them, else its verbs), a set past the type's first falling back to the first.
 *
 * @param types The game's object types.
 * @param sets Each type's sound sets; the game's by default.
 * @returns One source per set, in the types' order.
 */
export function thingHookSources(types: readonly ObjectTypeDefinition[], sets: ThingSoundSets = THING_SOUND_SETS): ThingHookSource[] {
  const sources: ThingHookSource[] = [];
  for (const type of types) {
    const listed = sets[type.type] ?? [];
    const events = THING_EVENT_SOUNDS[type.type] ?? thingEvents(type);
    listed.forEach((set, index) => {
      sources.push({ set, events: [...events], fallback: index === 0 ? null : (listed[0] as string) });
    });
  }
  return sources;
}
